/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import 'whatwg-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import { ApolloProvider } from '@apollo/react-hooks';
import { configureAnchors } from 'react-scrollable-anchor';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';
import { ThemeProvider } from '@material-ui/styles';
import isInvalidBrowser, {
  getBrowserName,
} from 'shared/utilities/detectBrowser';
import { getCurrTimeZone, isValidTimeZone } from 'shared/utilities/time-zone';
import theme from 'shared/theme/theme';
import { ProfileType, ProductContext } from './shared/constants';
import { setUserTimeZone, setUrlPath } from './shared/actions/subscriptions';
import App from './shared/App';
import createFetch from './createFetch';
import configureStore from './shared/store/configureStore';
import { updateMeta, updateCustomMeta } from './DOMUtils';
import history from './history';
import createApolloClient from './core/createApolloClient';
import getRouter from './router';

// Universal HTTP client
const fetch = createFetch(window.fetch, {
  baseUrl: window.App.apiUrl,
});

const apolloClient = createApolloClient(
  window.App.tasUrl,
  window.App.tasWsUrl,
  window.App.apiToken,
);

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  // For react-apollo
  client: apolloClient,
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: configureStore(window.App.state, { fetch, history }),
  fetch,
  developmentEnvironment: window.App.developmentEnvironment,
};

const container = document.getElementById('app');
let currentLocation = history.location;
let appInstance;

const scrollPositionsHistory = {};

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  configureAnchors({ offset: -60, scrollDuration: 200 });
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  const isInitialRender = !action;
  try {
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);
    context.localStorage = window.localStorage;

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await getRouter().resolve(context);

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    // Set path name in redux
    await context.store.dispatch(setUrlPath(context.pathname));

    const renderReactApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render;
    appInstance = renderReactApp(
      <ThemeProvider theme={theme}>
        {/* allows us to use hooks within the application */}
        <ApolloProvider client={apolloClient}>
          <App context={context}>{route.component}</App>
        </ApolloProvider>
      </ThemeProvider>,
      container,
      () => {
        if (isInitialRender) {
          global.isInvalidBrowser = isInvalidBrowser(window);
          global.browserName = getBrowserName(window);

          // Switch off the native scroll restoration behavior and handle it manually
          // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
          if (window.history && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
          }

          const elem = document.getElementById('css');
          if (elem) elem.parentNode.removeChild(elem);

          const elem2 = document.getElementById('MUICSS');
          if (elem2 && elem2.parentNode) elem2.parentNode.removeChild(elem2);

          const { currentUser } = context.store.getState().subscriptions;
          if (currentUser) {
            const isTutor = currentUser.profile.profileTypes.includes(
              ProfileType.TUTOR,
            );

            if (isTutor) {
              window.analytics.identify(currentUser.id, {
                email: currentUser.email.address,
                firstName: currentUser.profile.firstName,
                lastName: currentUser.profile.lastName,
                phone: currentUser.profile.phoneNumber,
                title: 'Tutor',
                gradeLevel: currentUser.profile.gradeLevel,
                school: currentUser.profile.school,
              });
            } else {
              window.analytics.identify(currentUser.id, {
                email: currentUser.email.address,
                firstName: currentUser.profile.firstName,
                lastName: currentUser.profile.lastName,
                phone: currentUser.profile.phoneNumber,
                title: 'Client',
              });
            }

            if (!isValidTimeZone(currentUser.profile.timeZoneId)) {
              context.store.dispatch(
                setUserTimeZone(apolloClient, getCurrTimeZone()),
              );
            }

            // Track if the user has just logged in. The login fragment is attached only
            // for the Facebook and Google OAuth login routes.
            if (window.location.hash === '#login') {
              window.analytics.track('Logged In');
            }
          }
          return;
        }

        document.title = route.title;

        updateMeta('description', route.description);
        // Update necessary tags in <head> at runtime here, ie:

        updateMeta('keywords', route.keywords);
        if (route.canonicalUrl) {
          updateCustomMeta('og:url', route.canonicalUrl);
        }
        if (route.imageUrl) {
          updateCustomMeta('og:image', route.imageUrl);
        }
        // updateLink('canonical', route.canonicalUrl);
        // etc.

        let scrollX = 0;
        let scrollY = 0;
        const pos = scrollPositionsHistory[location.key];
        if (pos) {
          scrollX = pos.scrollX;
          scrollY = pos.scrollY;
        } else {
          const targetHash = location.hash.substr(1);
          if (targetHash) {
            const target = document.getElementById(targetHash);
            if (target) {
              scrollY = window.pageYOffset + target.getBoundingClientRect().top;
            }
          }
        }

        // Restore the scroll position if it was saved into the state
        // or scroll to the given #hash anchor
        // or scroll to top of the page
        window.scrollTo(scrollX, scrollY);

        // Segment tracking. Initial page view render setting is sent
        // in the Html component.
        if (window.analytics) {
          const internalReferer = location.state
            ? location.state.prevURL || ''
            : '';

          const productContext = ProductContext.MARKETPLACE;
          if (internalReferer.length > 0) {
            window.analytics.page(productContext, route.title, {
              referrer: internalReferer,
              productContext,
            });
          } else {
            window.analytics.page(productContext, route.title, {
              productContext,
            });
          }
        }
      },
    );
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('RSK will reload your page after error');
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  // module.hot.accept('./router', () => {
  module.hot.accept(getRouter(), () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}
